import * as React from "react";
import { CalendarIcon, X } from "lucide-react";
import { format, parse, isValid, addDays, addYears, startOfMonth, addMonths } from "date-fns";
import { it } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * 🆕 DatePicker localizzato (IT · DD/MM/YYYY) drop-in per `<input type="date">`.
 *
 *  Uso:
 *    <DatePicker value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} />
 *
 *  API:
 *   - value: stringa ISO YYYY-MM-DD (come input[type=date])
 *   - onChange: riceve un evento sintetico { target: { value: "YYYY-MM-DD" } }
 *     così è compatibile con il pattern classico onChange={(e) => ...}
 *   - min / max: opzionali, formato ISO YYYY-MM-DD
 *   - disabled, className, placeholder, data-testid: passati al trigger
 *   - clearable (default true): mostra la X per svuotare il campo
 */
export function DatePicker({
  value,
  onChange,
  min,
  max,
  disabled,
  placeholder = "gg/mm/aaaa",
  className,
  clearable = true,
  fromYear,
  toYear,
  ...rest
}) {
  const [open, setOpen] = React.useState(false);

  // Range anni default: da 1920 al 2050 (copre nascite storiche fino a scadenze future).
  // Personalizzabile via prop fromYear/toYear.
  const currentYear = new Date().getFullYear();
  fromYear = fromYear ?? 1920;
  toYear = toYear ?? (currentYear + 25);

  // Parse ISO string → Date object
  const parsed = React.useMemo(() => {
    if (!value) return null;
    // Accetta "YYYY-MM-DD" o "YYYY-MM-DDTHH:mm:ss" (taglia al primo T)
    const s = String(value).slice(0, 10);
    const d = parse(s, "yyyy-MM-dd", new Date());
    return isValid(d) ? d : null;
  }, [value]);

  const minDate = React.useMemo(() => min ? parse(String(min).slice(0, 10), "yyyy-MM-dd", new Date()) : undefined, [min]);
  const maxDate = React.useMemo(() => max ? parse(String(max).slice(0, 10), "yyyy-MM-dd", new Date()) : undefined, [max]);

  const emit = (isoValue) => {
    // Simula l'evento nativo dell'input HTML per drop-in con onChange={(e)=>e.target.value}
    if (typeof onChange === "function") {
      onChange({ target: { value: isoValue } });
    }
  };

  const handleSelect = (day) => {
    if (!day) { emit(""); setOpen(false); return; }
    const iso = format(day, "yyyy-MM-dd");
    emit(iso);
    setOpen(false);
  };

  // 🆕 Gestione input manuale: accetta DD/MM/YYYY o DDMMYYYY
  const [typing, setTyping] = React.useState("");
  React.useEffect(() => { setTyping(parsed ? format(parsed, "dd/MM/yyyy") : ""); }, [parsed]);

  const applyTyped = (raw) => {
    const s = String(raw || "").trim();
    if (!s) { emit(""); return; }
    // Formati accettati: DD/MM/YYYY, D/M/YYYY, DDMMYYYY, DD-MM-YYYY
    const cleaned = s.replace(/[^0-9]/g, "");
    let d = null;
    if (cleaned.length === 8) {
      d = parse(cleaned, "ddMMyyyy", new Date());
    } else if (cleaned.length === 6) {
      // DDMMYY → assumiamo 19YY per anni >=30, altrimenti 20YY
      const yy = parseInt(cleaned.slice(4), 10);
      const century = yy >= 30 ? 1900 : 2000;
      d = parse(cleaned.slice(0, 4) + (century + yy), "ddMMyyyy", new Date());
    } else {
      // Prova pattern con separatori
      for (const fmt of ["dd/MM/yyyy", "d/M/yyyy", "dd-MM-yyyy", "d-M-yyyy"]) {
        const t = parse(s, fmt, new Date());
        if (isValid(t)) { d = t; break; }
      }
    }
    if (d && isValid(d)) {
      if (minDate && d < minDate) return;
      if (maxDate && d > maxDate) return;
      emit(format(d, "yyyy-MM-dd"));
    }
  };

  const formatTyping = (v) => {
    // Auto-format: 05072026 → 05/07/2026; 5/7/26 lascia stare
    const digits = String(v || "").replace(/[^0-9/]/g, "");
    if (digits.length === 8 && !digits.includes("/")) {
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    }
    return digits;
  };

  const handleClear = () => {
    // Kept for parity; the visible clear button uses inline handler
    emit("");
    setTyping("");
  };
  void handleClear;

  const label = parsed ? format(parsed, "dd/MM/yyyy", { locale: it }) : "";
  void label; // no longer displayed as static text; keep for future use

  return (
    <div className={cn("relative flex items-center", className)}>
      <input
        type="text"
        inputMode="numeric"
        value={typing}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => setTyping(formatTyping(e.target.value))}
        onBlur={() => applyTyped(typing)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); applyTyped(typing); e.currentTarget.blur(); }
        }}
        maxLength={10}
        className={cn(
          "flex h-9 w-full items-center rounded-md border border-input bg-white pl-3 pr-16 py-2 text-sm ring-offset-background tabular-nums",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
        {...rest}
      />
      <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
        {clearable && typing && !disabled && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setTyping(""); emit(""); }}
            className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-rose-600"
            title="Svuota"
            data-testid="datepicker-clear"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <Popover open={open && !disabled} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              className="p-1.5 rounded hover:bg-blue-50 text-blue-500 hover:text-blue-700 transition-colors"
              title="Apri calendario"
              data-testid="datepicker-open"
            >
              <CalendarIcon className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              locale={it}
              captionLayout="dropdown-buttons"
              fromYear={fromYear}
              toYear={toYear}
              selected={parsed || undefined}
              defaultMonth={parsed || undefined}
              onSelect={handleSelect}
              disabled={(date) => {
                if (minDate && date < minDate) return true;
                if (maxDate && date > maxDate) return true;
                return false;
              }}
              initialFocus
              classNames={{
                caption_label: "hidden",
                vhidden: "sr-only",
                caption_dropdowns: "flex items-center gap-1.5",
                dropdown: "h-8 rounded-md border border-slate-200 bg-white px-2 pr-6 text-sm font-medium hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 cursor-pointer",
                dropdown_month: "text-sm",
                dropdown_year: "text-sm",
              }}
            />
            <div className="border-t border-slate-200 p-2 flex flex-wrap gap-1.5 bg-slate-50" data-testid="datepicker-shortcuts">
              {[
                { label: "Oggi", get: () => new Date() },
                { label: "Domani", get: () => addDays(new Date(), 1) },
                { label: "+30 gg", get: () => addDays(new Date(), 30) },
                { label: "1° prossimo mese", get: () => startOfMonth(addMonths(new Date(), 1)) },
                { label: "+1 anno", get: () => addYears(new Date(), 1) },
              ].map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => handleSelect(s.get())}
                  className="text-[11px] font-medium px-2 py-1 rounded border border-slate-200 bg-white text-slate-700 hover:border-indigo-400 hover:text-indigo-700 transition-colors"
                  data-testid={`datepicker-shortcut-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

export default DatePicker;
